import { ActionTree } from 'vuex'
import { RootState } from '@/store'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'
import { CompanyAPI, PipelineAPI, ReferralAPI } from '@/api/backend-client'
import { ReferralsState, ReferralsStateFilters } from '@/store/referrals'
import { PipelinePhaseCategory, ReferralAge, ReferralFilterDirection, ReferralStatus, ReferralView } from '@/api/types'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import { StatusReason } from '@/api/model/status-reason'
import { Source } from '@/api/model/source'
import axios from 'axios'

let fetchDataCancelTokenSource
let loadPipelinesCancelTokenSource
let loadSearchAlternativesCancelTokenSource
let updateMenuCountCancelTokenSource
let updateNewMenuCountCancelTokenSource

export const actions: ActionTree<ReferralsState, RootState> = {
    async fetchData({ getters, commit, state, dispatch }): Promise<void> {
        if (fetchDataCancelTokenSource) {
            fetchDataCancelTokenSource.cancel()
        }

        fetchDataCancelTokenSource = axios.CancelToken.source()

        try {

            // load pipelines if needed
            if (CompanyAPI.currentCompany.pipelinesEnabled ?? false) {
                dispatch('loadPipelines')
            }

            commit('fetchData')
            commit('clearSearchAlternatives')

            const referrals = await ReferralAPI.getPaged(getters.requestParams, fetchDataCancelTokenSource.token)
            if (referrals === undefined) {
                return
            }

            commit('fetchDataSuccess', referrals)

            // stop if we have results
            if (state.referrals.length > 0) {
                return
            }

            // stop if we aren't fuzzy searching
            if (!state.search || state.search.length === 0) {
                return
            }

            // load additional search alternatives
            dispatch('loadSearchAlternatives')

        } catch (error) {
            commit('fetchDataFailure', error)
        }
    },

    async loadPipelines({ commit }): Promise<void> {
        if (loadPipelinesCancelTokenSource) {
            loadPipelinesCancelTokenSource.cancel()
        }

        loadPipelinesCancelTokenSource = axios.CancelToken.source()

        try {
            commit('loadPipelines')

            const pipelines = await PipelineAPI.getAll({ company: CompanyAPI.currentCompany.id }, loadPipelinesCancelTokenSource.token)
            if (pipelines === undefined) {
                return
            }

            commit('loadPipelinesSuccess', pipelines)

            const outgoingConnections = await CompanyAPI.getOutgoingConnections(loadPipelinesCancelTokenSource.token)
            if (outgoingConnections === undefined) {
                return
            }

            const outgoingPipelines = outgoingConnections
                .filter((connection: PipelineConnection) => connection.sourceEnabled)
                .filter((connection: PipelineConnection) => connection.targetEnabled)
                .filter((connection: PipelineConnection) => connection.pipeline !== null)
                .map((connection: PipelineConnection) => connection.pipeline)

            commit('loadOutgoingPipelinesSuccess', outgoingPipelines)

        } catch (error) {
            commit('loadPipelinesFailure', error)
        }
    },

    async loadSearchAlternatives({ commit, state }): Promise<void> {
        if (loadSearchAlternativesCancelTokenSource) {
            loadSearchAlternativesCancelTokenSource.cancel()
        }

        loadSearchAlternativesCancelTokenSource = axios.CancelToken.source()

        try {
            commit('loadingSearchAlternatives')

            const params = {
                view: state.view,
                search: state.search,
                page: 1,
                pagination: true,
                itemsPerPage: state.itemsPerPage,
                status: null,
                type: null,
            }

            const [ userIn, userOut, teamIn, teamOut ] = await Promise.all([
                ReferralAPI.getPaged({ ...params, view: 'user_in' }, loadSearchAlternativesCancelTokenSource.token),
                ReferralAPI.getPaged({ ...params, view: 'user_out' }, loadSearchAlternativesCancelTokenSource.token),
                ReferralAPI.getPaged({ ...params, view: 'branches_in' }, loadSearchAlternativesCancelTokenSource.token),
                ReferralAPI.getPaged({ ...params, view: 'branches_out' }, loadSearchAlternativesCancelTokenSource.token),
            ])

            if (userIn === undefined || userOut === undefined || teamIn === undefined || teamOut === undefined) {
                return
            }

            commit('loadingSearchAlternativesSuccess', {
                userIn: userIn.collection,
                userOut: userOut.collection,
                teamIn: teamIn.collection,
                teamOut: teamOut.collection,
            })

        } catch (error) {
            commit('loadingSearchAlternativesFailure')
        }
    },

    async setPage({ commit, dispatch }, payload: { page: number }): Promise<void> {
        commit('setPage', payload)
        dispatch('fetchData')
    },

    async nextPage({ state, commit, dispatch }): Promise<void> {
        commit('setPage', { page: state.page + 1 })
        dispatch('fetchData')
    },

    async prevPage({ state, commit, dispatch }): Promise<void> {
        commit('setPage', { page: state.page - 1 })
        dispatch('fetchData')
    },

    async setItemsPerPage({ commit, dispatch }, payload: { itemsPerPage: number }): Promise<void> {
        commit('setItemsPerPage', payload)
        commit('setPage', { page: 1 })
        dispatch('fetchData')
    },

    async setDisplayMode({ commit, dispatch }, payload: { displayMode: string }): Promise<void> {
        commit('setDisplayMode', payload)
    },

    async applyView({ state, getters, commit, dispatch }, payload: { view: ReferralView, direction: ReferralFilterDirection, update?: boolean }): Promise<void> {
        commit('applyView', payload)

        // Reset pipeline filter if selected pipeline is no longer in selectable pipelines (ie. outgoing pipeline)
        if (state.pipelineFilter && !getters.selectedPipeline) {
            commit('applyPipelineFilter', { id: null })
        }

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applySearch({ commit, dispatch }, payload: { query: string }): Promise<void> {
        commit('applySearch', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyOrder({ commit, dispatch }, payload: { field: string, direction: string }): Promise<void> {
        commit('applyOrder', payload)
        commit('setPage', { page: 1 })
        dispatch('fetchData')
    },

    async applyPriorityFilter({ commit, dispatch }, payload: { priorityFilter: number[] }): Promise<void> {
        commit('applyPriorityFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyTypeFilter({ commit, dispatch }, payload: { typeFilter: string[], update?: boolean }): Promise<void> {
        commit('applyTypeFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', {page: 1})
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyStatusFilter({ commit, dispatch }, payload: { statusFilter: string, update?: boolean }): Promise<void> {
        commit('applyStatusFilter', payload)
        commit('setPage', { page: 1 })
        commit('resetNewStatusReasonFilter')

        if (payload.update ?? true) {
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyStatusReasonFilter({ commit, dispatch }, payload: { statusReasonFilter: string[] }): Promise<void> {
        commit('applyStatusReasonFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyNewStatusReasonFilter({ commit, dispatch }, payload: { newStatusReasonFilter: StatusReason[] }): Promise<void> {
        commit('applyNewStatusReasonFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyReferredByFilter({ commit, dispatch}, payload: { referredByFilter: User[], update?: boolean }): Promise<void> {
        commit('applyReferredByFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyReferredByBranchFilter({ commit, dispatch}, payload: { referredByBranchFilter: Branch[] }): Promise<void> {
        commit('applyReferredByBranchFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyAssignedToFilter({ commit, dispatch}, payload: { assignedToFilter: User[], update?: boolean }): Promise<void> {
        commit('applyAssignedToFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyAssignedToBranchFilter({ commit, dispatch}, payload: { assignedToBranchFilter: Branch[] }): Promise<void> {
        commit('applyAssignedToBranchFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyChannelFilter({ commit, dispatch }, payload: { channelFilter: string[] }): Promise<void> {
        commit('applyChannelFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyNewSourceFilter({ commit, dispatch }, payload: { newSourceFilter: Source[] }): Promise<void> {
        commit('applyNewSourceFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyScoreFilter({ commit, dispatch }, payload: { scoreFilter: number[] }): Promise<void> {
        commit('applyScoreFilter', payload)
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyReferredByUserFilter({ commit, dispatch }, payload: { user: User }): Promise<void> {
        commit('applyReferredByFilter', { referredByFilter: payload.user })
        commit('applyAssignedToFilter', { assignedToFilter: null })
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyAssignedToUserFilter({ commit, dispatch }, payload: { user: User }): Promise<void> {
        commit('applyReferredByFilter', { referredByFilter: null })
        commit('applyAssignedToFilter', { assignedToFilter: payload.user })
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyDateSpecificFilter({ commit, dispatch }, payload: { day: Date }): Promise<void> {
        commit('applyDateSpecificFilter', payload)
        commit('applyDateRangeFilter', { range: null })
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyDateRangeFilter({ commit, dispatch }, payload: { range: string, update?: boolean }): Promise<void> {
        commit('applyDateRangeFilter', payload)
        commit('applyAgeFilter', { age: null })
        commit('applyDateSpecificFilter', { day: null })

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyAgeFilter({ commit, dispatch }, payload: { ageFilter: ReferralAge }): Promise<void> {
        commit('applyAgeFilter', payload)
        commit('applyDateRangeFilter', { range: 'all' })
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async applyPipelineFilter({ commit, dispatch, getters, state }, payload: { id: string, update?: boolean }): Promise<void> {
        // clear state
        dispatch('applyPipelinePhaseCategoryFilter', { category: null, update: false })
        dispatch('applyPipelinePhaseFilter', { phase: null, update: false })

        // commit action
        commit('applyPipelineFilter', payload)

        // auto-select first tab
        if (getters.selectedPipeline) {
            dispatch('autoSelectPipelinePhaseFilter', { update: false })
        } else {
            dispatch('autoSelectPipelinePhaseCategoryFilter', { update: false })
        }

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyPipelinePhaseFilter({ commit, dispatch }, payload: { phase: PipelinePhase, update?: boolean }): Promise<void> {
        commit('applyPipelinePhaseFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async applyPipelinePhaseCategoryFilter({ commit, dispatch }, payload: { category: PipelinePhaseCategory, update?: boolean }): Promise<void> {
        commit('applyPipelinePhaseCategoryFilter', payload)

        if (payload.update ?? true) {
            commit('setPage', { page: 1 })
            dispatch('updateMenuCount')
            dispatch('fetchData')
        }
    },

    async doAutoSelect({ state, getters, dispatch }): Promise<void> {
        if (CompanyAPI.currentCompany.pipelinesEnabled ?? false) {
            const finalStatuses = [
                ReferralStatus.PROCESSED,
                ReferralStatus.SUCCESSFUL,
                ReferralStatus.CLOSED,
            ]

            if (!finalStatuses.includes(state.statusFilter as ReferralStatus)) {
                if (getters.selectedPipeline) {
                    dispatch('autoSelectPipelinePhaseFilter', { update: false, overwrite: false })
                } else {
                    dispatch('autoSelectPipelinePhaseCategoryFilter', { update: false, overwrite: false })
                }
            }
        }
    },

    async autoSelectPipelinePhaseFilter({ commit, dispatch, getters, state }, payload: { update?: boolean, overwrite?: boolean }): Promise<void> {
        const overwrite = payload.overwrite ?? true
        if (!overwrite && state.phaseFilter) {
            return
        }

        // auto-select first phase with content
        const phaseIds = getters.selectedPipeline.phases.map(selectedPipelinePhase => selectedPipelinePhase.id)
        const phaseId = state.referralStats.firstPhaseWithContent(phaseIds)

        let phase = getters.selectedPipeline.phases
            .find(v => v.id === phaseId)

        // if no category with content, try to select a status
        if (!phase) {
            const firstStatusWithContent = state.referralStats.firstStatusWithContent()

            if (firstStatusWithContent) {
                dispatch('applyStatusFilter', { statusFilter: firstStatusWithContent, update: true })
                return
            }
        }
        // otherwise auto-select first phase
        if (!phase && state.statusFilter !== ReferralStatus.OPEN) {
            phase = getters.selectedPipeline.firstPhase
        }

        if (phase) {
            dispatch('applyPipelinePhaseFilter', { phase, update: payload.update ?? true })
        }
    },

    async autoSelectPipelinePhaseCategoryFilter({ commit, dispatch, getters, state }, payload: { update?: boolean, overwrite?: boolean }): Promise<void> {
        const overwrite = payload.overwrite ?? true
        if (!overwrite && state.phaseCategoryFilter) {
            return
        }

        // auto-select first category with content
        let category = state.referralStats.firstCategoryWithContent()

        // if no category with content, try to select a status
        if (!category) {
            const firstStatusWithContent = state.referralStats.firstStatusWithContent()

            if (firstStatusWithContent) {
                dispatch('applyStatusFilter', { statusFilter: firstStatusWithContent, update: true })
                return
            }
        }

        if (!CompanyAPI.currentCompany.pipelinePhaseShowAllTab) {
            // otherwise auto-select first category
            if (!category) {
                category = PipelinePhaseCategory.ENTRY
            }

            if (category) {
                dispatch('applyPipelinePhaseCategoryFilter', { category, update: payload.update ?? true })
            }
        }
    },

    async applyFilters({ commit, dispatch }, payload: ReferralsStateFilters): Promise<void> {
        commit('clearFilters')
        commit('applyFilters', payload)
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async resetFilters({ commit, dispatch }): Promise<void> {
        commit('resetFilters')
        commit('setPage', { page: 1 })
        dispatch('updateMenuCount')
        dispatch('fetchData')
    },

    async snapshot({ commit }): Promise<void> {
        commit('snapshot')
    },

    async updateMenuCount({ state, getters, commit, dispatch }): Promise<void> {
        if (updateMenuCountCancelTokenSource) {
            updateMenuCountCancelTokenSource.cancel()
        }

        updateMenuCountCancelTokenSource = axios.CancelToken.source()

        try {
            if (CompanyAPI.currentCompany.pipelinesEnabled ?? false) {
                dispatch('updateNewMenuCount')
                return
            }

            const counts = await ReferralAPI.getReferralCountForStatus(
                state.statusFilter as ReferralStatus,
                getters.requestParams,
                updateMenuCountCancelTokenSource.token,
            )

            if (counts === undefined) {
                return
            }

            commit('updateMenuCount')
            commit('updateMenuCountSuccess', counts)

            // auto-select first-tab
            dispatch('doAutoSelect')

        } catch (error) {
            commit('updateMenuCountFailure', error)
        }
    },

    async updateNewMenuCount({ state, getters, commit, dispatch }): Promise<void> {
        if (updateNewMenuCountCancelTokenSource) {
            updateNewMenuCountCancelTokenSource.cancel()
        }

        updateNewMenuCountCancelTokenSource = axios.CancelToken.source()

        try {
            const counts = await ReferralAPI.getCounts(
                state.pipelineFilter,
                state.phaseFilter ? state.phaseFilter.id : null,
                state.statusFilter,
                state.phaseCategoryFilter,
                getters.requestParams,
                updateNewMenuCountCancelTokenSource.token,
            )

            if (counts === undefined) {
                return
            }

            commit('updateNewMenuCount')
            commit('updateNewMenuCountSuccess', counts)

            // auto-select first tab
            dispatch('doAutoSelect')

        } catch (error) {
            commit('updateNewMenuCountFailure', error)
        }
    },
}
